import React, { useState, useEffect, useContext } from "react";
import {
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  Typography,
  Collapse,
  Box,
  CssBaseline,
  Container,
  Card,
  CardContent,
  CardActions,
  Divider,
  IconButton,
  Tabs,
  Tab,
  Alert,
  useMediaQuery
} from "@mui/material";
import { Info,Print, ExpandMore, ExpandLess } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import Layout from "./Layout";
import { enGB } from "date-fns/locale";
import { startOfToday, startOfDay, endOfDay, format } from "date-fns";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "./firebase";
import AppContext from "./AppContext";
import { Modal } from "@mui/material";
import SaleSummaryPrint from "./components/SaleSummaryPrint";

const ViewSales = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [expandedSaleId, setExpandedSaleId] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState(startOfToday());
  const [toDate, setToDate] = useState(startOfToday());
  const { shopId, t } = useContext(AppContext);
  const [tabIndex, setTabIndex] = useState(0);
  const [isOffline, setIsOffline] = useState(false);
  const [showSaleSummary, setShowSaleSummary] = useState(false);
  const [saleDetails, setSaleDetails] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const showTableView = !isMobile && !isTablet;

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    setIsOffline(!navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const fetchSales = async () => {
      const salesRef = collection(db, "sales");
      let q;
  
      if (searchQuery) {
        // Search by exact orderId or partial match
        q = query(
          salesRef,
          where("orderId", ">=", searchQuery),
          where("orderId", "<=", searchQuery + "\uf8ff"),
          where("shopId", "==", shopId),
          orderBy("orderId")
        );
      } else {
        // Default query with date range when no search query
        q = query(
          salesRef,
          where("orderDate", ">=", startOfDay(fromDate)),
          where("orderDate", "<=", endOfDay(toDate)),
          where("shopId", "==", shopId),
          orderBy("orderDate", "desc")
        );
      }
  
      try {
        const querySnapshot = await getDocs(q);
        const sales = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        setSalesData(sales);
        console.log("sale data is", sales);
      } catch (error) {
        console.log("Error fetching sales:", error);
      }
    };
  
    fetchSales();
  }, [fromDate, toDate, shopId, searchQuery]);

  const handleChangeTab = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleSaleClick = (saleId) => {
    setExpandedSaleId(expandedSaleId === saleId ? null : saleId);
  };

  const filteredSales = salesData.filter(sale => 
    tabIndex === 0 ? sale.transactionType === "income" : sale.transactionType !== "income"
  );

  const formatToMyanmarCurrency = (number) => {
    return new Intl.NumberFormat('my-MM').format(number);
  };

  const handleActionClick = (action, saleId) => {
    const selectedSale = salesData.find(sale => sale.orderId === saleId);
    if (action === "print") {
      const mappedSaleDetails = {
        ...selectedSale,
        cartItems: selectedSale.items.map(item => ({
          name: item.productName,
          selectedVariant: item.stockCode,
          quantity: item.quantity,
          price: item.price,
          variantPrice: 0,  // Assuming no additional variant pricing
          totalPrice: item.total
        }))
      };
      setSaleDetails(mappedSaleDetails);
      setShowSaleSummary(true);
    }
  };
  
  const handleCloseSaleSummary = () => {
    setShowSaleSummary(false);
    setSaleDetails(null);
  };

  const renderSaleDetails = (sale) => (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      {sale.items && sale.items.length > 0 && (
        <Table size={showTableView ? "medium" : "small"}>
          <TableHead>
            <TableRow>
              <TableCell><Typography fontWeight="bold">{t("Item Name")}</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">{t("Stock Code")}</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">{t("Quantity")}</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">{t("Price")}</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight="bold">{t("Amount")}</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sale.items.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{item.productName}</TableCell>
                <TableCell>{item.stockCode}</TableCell>
                <TableCell>{item.quantity}</TableCell>
                <TableCell>{formatToMyanmarCurrency(item.price)}</TableCell>
                <TableCell align="right">{formatToMyanmarCurrency(item.total)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={4} align="right" sx={{ p: 0.5, border: 0 }}>
                <strong>{t("Payment Method")}</strong>
              </TableCell>
              <TableCell align="right" sx={{ p: 0.5, border: 0 }}>
                <strong>{sale.paymentMethod}</strong>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={4} align="right" sx={{ p: 0.5, border: 0 }}>
                <strong>{t("Cashier")}</strong>
              </TableCell>
              <TableCell align="right" sx={{ p: 0.5, border: 0 }}>
                <strong>{sale.cashierName}</strong>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={4} align="right" sx={{ p: 0.5, border: 0 }}>
                <strong>{t("Subtotal")}</strong>
              </TableCell>
              <TableCell align="right" sx={{ p: 0.5, border: 0 }}>
                <strong>{formatToMyanmarCurrency(sale.subtotal)}</strong>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={4} align="right" sx={{ p: 0.5, border: 0 }}>
                <strong>{t("Tax")}</strong>
              </TableCell>
              <TableCell align="right" sx={{ p: 0.5, border: 0 }}>
                <strong>{formatToMyanmarCurrency(sale.tax)}</strong>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={4} align="right" sx={{ p: 0.5, border: 0 }}>
                <strong>{t("Total")}</strong>
              </TableCell>
              <TableCell align="right" sx={{ p: 0.5, border: 0 }}>
                <strong>{formatToMyanmarCurrency(sale.amount)}</strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Layout>
        <Box sx={{ p: 3,mt:0 }}>
        {isOffline && (
                <Alert severity="warning">
                  {t("You are offline. Some features may not be available.")}
                </Alert>
              )}
          <Typography variant="h4" mb={2}>
           {t('View Sales')}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
            <Box>
           
              <Tabs value={tabIndex} onChange={handleChangeTab} aria-label={t("Sales Tabs")}>
                <Tab label={t("Sales Record")} />
              {/*<Tab label="Other Transactions" /> */}  
              </Tabs>

              <Grid container spacing={2} alignItems="center" mt={1}>
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    label={t("From")}
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    label={t("To")}
                    value={toDate}
                    onChange={(newValue) => setToDate(newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={4}>
                  <TextField
                    label={t("Search by Sale ID")}
                    variant="outlined"
                    onChange={(e) => setSearchQuery(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>

              {showTableView ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><Typography variant="subtitle1" fontWeight="bold">{t("Sale ID")}</Typography></TableCell>
                        <TableCell><Typography variant="subtitle1" fontWeight="bold">{t("Date")}</Typography></TableCell>
                        <TableCell><Typography variant="subtitle1" fontWeight="bold">{t("Description")}</Typography></TableCell>
                        <TableCell><Typography variant="subtitle1" fontWeight="bold">{t("Amount")}</Typography></TableCell>
                        <TableCell><Typography variant="subtitle1" fontWeight="bold">{t("Actions")}</Typography></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSales
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((sale) => (
                          <React.Fragment key={sale.orderId}>
                            <TableRow
                              onClick={() => handleSaleClick(sale.orderId)}
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell>{sale.orderId}</TableCell>
                              <TableCell>{format(sale.orderDate.toDate(), "dd-MM-yyyy hh:mm a")}</TableCell>
                              <TableCell>{sale.description}</TableCell>
                              <TableCell>{formatToMyanmarCurrency(sale.amount)}</TableCell>
                              <TableCell>
                                <IconButton
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleActionClick("print", sale.orderId);
                                  }}
                                  color="primary"
                                >
                                  <Print />
                                </IconButton>
                                <IconButton color="info">
                                  <Info />
                                </IconButton>
                              </TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: "none" }} colSpan={6}>
                                <Collapse in={expandedSaleId === sale.orderId} timeout="auto" unmountOnExit>
                                  <Box sx={{ m: 1, p: 1, borderRadius: 1, border: 1, borderColor: "divider" }}>
                                    {renderSaleDetails(sale)}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Grid container spacing={2} mt={0.5}>
                  {filteredSales
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((sale) => {
                      const isExpanded = expandedSaleId === sale.orderId;
                      return (
                        <Grid item xs={12} key={sale.orderId}>
                          <Card
                            variant="outlined"
                            onClick={() => handleSaleClick(sale.orderId)}
                            sx={{ cursor: "pointer" }}
                          >
                            <CardContent>
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography variant="subtitle2" color="text.secondary">
                                    {t("Sale ID")}
                                  </Typography>
                                  <Typography variant="h6">{sale.orderId}</Typography>
                                </Box>
                                <IconButton
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleActionClick("print", sale.orderId);
                                  }}
                                  color="primary"
                                >
                                  <Print />
                                </IconButton>
                              </Box>
                              <Typography variant="body2" color="text.secondary" mt={1}>
                                {format(sale.orderDate.toDate(), "dd-MM-yyyy hh:mm a")}
                              </Typography>
                              <Typography variant="body1" mt={1}>
                                {sale.description || "No description"}
                              </Typography>
                              <Typography variant="h6" mt={1}>
                                {formatToMyanmarCurrency(sale.amount)} {t("MMK")}
                              </Typography>
                            </CardContent>
                            <CardActions sx={{ justifyContent: "space-between", px: 2 }}>
                              <Button
                                size="small"
                                color="info"
                                startIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                              >
                                {isExpanded ? "Hide Details" : "View Details"}
                              </Button>
                              <IconButton color="info">
                                <Info />
                              </IconButton>
                            </CardActions>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Divider />
                              <Box sx={{ p: 2 }}>
                                {renderSaleDetails(sale)}
                              </Box>
                            </Collapse>
                          </Card>
                        </Grid>
                      );
                    })}
                </Grid>
              )}

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredSales.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
              />

              <Modal
                open={showSaleSummary}
                onClose={handleCloseSaleSummary}
                aria-labelledby="sale-summary-modal"
                aria-describedby="sale-summary-description"
              >
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '80%',
                  maxWidth: 800,
                  bgcolor: 'background.paper',
                  boxShadow: 24,
                  p: 4,
                  maxHeight: '90vh',
                  overflowY: 'auto',
                }}>
                  <SaleSummaryPrint
                    saleDetails={saleDetails}
                    onClose={handleCloseSaleSummary}
                  />
                </Box>
              </Modal>
            </Box>
          </LocalizationProvider>
        </Box>
      </Layout>
    </ThemeProvider>
  );
};

export default ViewSales;
